import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine } from "@/components/anim";
import { HERO } from "@/data/content";

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen w-full overflow-hidden bg-bg-dark">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src="/assets/water-hero.jpg" alt="Desalinated water" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-bg-dark/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-bg-dark/40 via-transparent to-bg-dark" />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col justify-center"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-brand-teal-glow text-xs md:text-sm font-bold uppercase tracking-[0.3em] mb-6"
          data-testid="hero-overline"
        >
          {HERO.overline}
        </motion.p>

        <h1 className="font-heading font-extrabold uppercase text-white leading-[0.85] tracking-tighter text-5xl sm:text-7xl lg:text-[7.5vw]">
          <MaskedLine delay={0.35}>{HERO.line1}</MaskedLine>
          <MaskedLine delay={0.5}>{HERO.line2}</MaskedLine>
        </h1>

        <div className="mt-4 font-heading font-medium uppercase text-brand-teal tracking-tight text-2xl sm:text-3xl lg:text-[2.6vw] leading-[1.2]">
          <MaskedLine delay={0.65}>{HERO.line3}</MaskedLine>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-xl text-white/75 text-base md:text-lg leading-relaxed"
          data-testid="hero-sub"
        >
          {HERO.sub}
        </motion.p>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-0 w-full z-10"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center gap-3 text-white/60">
          <ArrowDown size={16} className="animate-bounce" />
          <span className="text-xs uppercase tracking-[0.25em]">Scroll to explore</span>
        </div>
      </motion.div>
    </section>
  );
}
